import type { Bauteilumfang, LueftungBestand, LueftungFeld } from '../../api';
import { formatiereZahl, type BaumKnoten, type Fachstatus } from '../../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TV6c - DIE REGELN DES LÜFTUNGSBESTANDS (ohne React, einzeln prüfbar)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Was die Seite „Lüftung“ aus dem Bestand bildet: den Baum links, den Träger
 * einer Wahl (Anlage, Verbundnetz, Geschoss ohne Zuordnung), Dateiname und
 * Umfang der Tabelle, die Feldgruppen und die Marken der Datenblätter.
 *
 * WICHTIG: `traegerVon` und `tabelle` bilden GENAU die Werte, die die Seite
 * vor TV6c gebildet hat - auch dort, wo sie fragwürdig sind (Befund §20.6):
 * Ein Geschoss ohne Zuordnung heißt „‹Geschoss› · ‹Zuordnung›“, und ohne
 * Geschoss ist es „null · …“ mit leerem Geschossfilter. Die Schreib- und
 * Einlesewege bleiben in TV6c unverändert; die Charakterisierung
 * `Lueftung.schreibwege.test.tsx` hält das fest.
 */

/** Der links gewählte Träger. */
export interface Traeger {
  art: 'anlage' | 'netz' | 'offen';
  /** Anlage, Netz - oder bei „offen“ das Geschoss ('' = ohne Geschoss). */
  id: string;
  name: string;
  /** Nur „offen“: die Zuordnung des Knotens (TV7a, §22 b). */
  zuordnung?: string;
}

/** Was an einem Baumknoten hängt. */
export type Bestandsknoten =
  | { art: 'gruppe' }
  | { art: 'traeger'; traeger: Traeger };

const n0 = (wert: number) => formatiereZahl(wert, 0);

/** Text der Zuordnung eines nicht zugeordneten Geschosses (vorher nur als Tooltip). */
export function zuordnungText(zuordnung: string): string {
  return zuordnung === 'ohne_system' ? 'nicht an das Kanalnetz angebunden' : 'keiner Anlage zugeordnet';
}

/**
 * Der Baum: Geschosse (nach dem Index, nie nach der Zeichenkette - sonst
 * stünde das Dach vor dem Erdgeschoss) mit ihren Anlagen, dann die
 * Verbundnetze und die nicht zugeordneten Geschosse als eigene Gruppen.
 * `suche` filtert Anlagen nach Name oder AKS.
 */
export function bestandKnoten(bestand: LueftungBestand, suche = ''): BaumKnoten<Bestandsknoten>[] {
  const t = suche.trim().toLowerCase();
  const gruppen = new Map<string, { index: number; anlagen: LueftungBestand['anlagen'] }>();
  for (const a of bestand.anlagen) {
    if (t && !a.anlage.toLowerCase().includes(t) && !(a.aksSchluessel ?? '').toLowerCase().includes(t)) continue;
    const name = a.ebene ?? 'ohne Geschoss';
    if (!gruppen.has(name)) gruppen.set(name, { index: a.ebeneIndex ?? 0, anlagen: [] });
    gruppen.get(name)!.anlagen.push(a);
  }

  const knoten: BaumKnoten<Bestandsknoten>[] = [...gruppen.entries()]
    .map(([name, g]) => ({ name, ...g }))
    .sort((x, y) => x.index - y.index)
    .map((g) => ({
      id: `e:${g.name}`,
      titel: g.name,
      daten: { art: 'gruppe' as const },
      anzahl: g.anlagen.length,
      kinder: g.anlagen.map((a) => ({
        id: `a:${a.id}`,
        titel: a.anlage,
        daten: { art: 'traeger' as const, traeger: { art: 'anlage' as const, id: a.id, name: a.anlage } },
        wert: `${n0(a.bauteile)} Bauteile`,
        hinweis: a.ebenenkonflikt ? 'Geschoss im Namen weicht ab' : undefined,
      })),
    }));

  // Verbundnetze gehören keinem Geschoss: Zehn Bowl-Zuluftgeräte speisen ein gemeinsames Netz.
  if (bestand.verbundnetze.length > 0) {
    knoten.push({
      id: 'g:netze',
      titel: 'Verbundnetze',
      daten: { art: 'gruppe' },
      anzahl: bestand.verbundnetze.length,
      kinder: bestand.verbundnetze.map((v) => ({
        id: `n:${v.id}`,
        titel: v.aksSchluessel ?? v.systemname,
        daten: { art: 'traeger' as const, traeger: { art: 'netz' as const, id: v.id, name: v.aksSchluessel ?? v.systemname } },
        wert: `${n0(v.anzahl)} Bauteile`,
        hinweis: `${n0(v.anlagenAnzahl)} Anlagen: ${v.anlagen.join(', ')}`,
      })),
    });
  }

  if (bestand.nichtZugeordnet.length > 0) {
    knoten.push({
      id: 'g:offen',
      titel: 'Nicht zugeordnet',
      daten: { art: 'gruppe' },
      anzahl: bestand.nichtZugeordnet.length,
      kinder: bestand.nichtZugeordnet.map((o) => ({
        id: `o:${o.ebene ?? ''}|${o.zuordnung}`,
        titel: o.ebene || 'ohne Geschoss',
        // TV7a (§22 a): „ohne Geschoss“ statt „null“ in Name und Dateiname; die Zuordnung geht in Liste und Tabelle.
        daten: {
          art: 'traeger' as const,
          traeger: { art: 'offen' as const, id: o.ebene ?? '', name: `${o.ebene || 'ohne Geschoss'} · ${o.zuordnung}`, zuordnung: o.zuordnung },
        },
        wert: `${n0(o.anzahl)} Bauteile`,
        hinweis: zuordnungText(o.zuordnung),
      })),
    });
  }

  return knoten;
}

/** Die Baum-Id eines Trägers (für `gewaehlt`). */
export function traegerKnotenId(t: Traeger | null): string | null {
  if (!t) return null;
  if (t.art === 'anlage') return `a:${t.id}`;
  if (t.art === 'netz') return `n:${t.id}`;
  return null; // Ein Geschoss ohne Zuordnung ist über Geschoss + Zuordnung eindeutig, nicht über die Kennung.
}

/** Dateiname und Umfang der Tabelle - wörtlich wie vor TV6c. */
export function tabelle(t: Traeger | null): {
  beschriftung: string;
  dateiname: string;
  umfang: Bauteilumfang | undefined;
} {
  return {
    beschriftung: t ? `Tabelle: ${t.name}` : 'Als Tabelle',
    dateiname: `Lueftung_${t?.name.replace(/[^\w.-]+/g, '_') ?? 'Gesamt'}.xlsx`,
    umfang: t ? bauteilAnfrage(t) : undefined,
  };
}

/**
 * Die Abfrage der Bauteile eines Trägers. Ein Knoten „Nicht zugeordnet“ (TV7a, §22 a/b) wählt genau seine Menge:
 * das Geschoss (oder ohne Geschoss), seine Zuordnung und nur die nicht zugeordneten Bauteile - bis TV7a das ganze
 * Geschoss bzw. bei „ohne Geschoss“ gar keinen Filter.
 */
export function bauteilAnfrage(t: Traeger): Bauteilumfang {
  if (t.art === 'anlage') return { plantId: t.id };
  if (t.art === 'netz') return { systemId: t.id };
  return {
    ...(t.id ? { ebene: t.id } : { ohneEbene: true }),
    ...(t.zuordnung ? { zuordnung: t.zuordnung } : {}),
    traeger: 'offen',
  };
}

/** Felder nach Fachgruppe, in der Reihenfolge des Katalogs. */
export function feldgruppen<F extends Pick<LueftungFeld, 'gruppe'>>(felder: F[]): { name: string; felder: F[] }[] {
  const gruppen: { name: string; felder: F[] }[] = [];
  for (const f of felder) {
    const name = f.gruppe ?? 'Weitere';
    const letzte = gruppen[gruppen.length - 1];
    if (letzte?.name === name) letzte.felder.push(f);
    else gruppen.push({ name, felder: [f] });
  }
  return gruppen;
}

/** Werte aus dem gespeicherten JSON - leer bei fehlendem. */
/**
 * TVX3 (übernommen in TV6d): Was beim Speichern an den Server geht - nur die geänderten Felder, als Text. Bis TVX3 ging
 * das ganze Werteverzeichnis zurück, mit den Zahlen der Rechnung als Zahl (Antwort 400 in 8000), und ohne Zahlen schrieb
 * der Server jeden mitgeschickten Schlüssel als „hand“ fest. Ein geleertes Feld geht als leerer Text und wird entfernt.
 */
export function nurGeaenderte(werte: Record<string, unknown>, schluessel: string[]): Record<string, string> {
  return Object.fromEntries(schluessel.map((k) => [k, werte[k] === undefined || werte[k] === null ? '' : String(werte[k])]));
}

export function werteAus(json: string | null | undefined): Record<string, string> {
  return json ? JSON.parse(json) as Record<string, string> : {};
}

/** Beschriftung eines Felds mit Einheit, wie in der Tabelle: „Nennmaß [mm]“. */
export function feldBeschriftung(f: Pick<LueftungFeld, 'label' | 'unit'>): string {
  return f.unit ? `${f.label} [${f.unit}]` : f.label;
}

const ZUSTAND_TEXT: Record<string, string> = {
  offen: 'offen',
  wartet: 'wartet',
  laeuft: 'läuft',
  gesucht: 'Datei abgelegt',
  gefunden: 'Vorschläge liegen vor',
  nicht_gefunden: 'nichts gefunden',
  geprueft: 'geprüft',
};

/** Ob ein Datenblatt gerade ausgewertet wird (dann lädt die Seite nach). */
export function inArbeit(zustand: string | null | undefined): boolean {
  return zustand === 'wartet' || zustand === 'laeuft';
}

/** Der Zustand eines Datenblatts als Marke (S1.5) - vorher `.marke gut/laufend/warnung/neutral`. */
export function datenblattStatus(zustand: string): Fachstatus {
  const text = ZUSTAND_TEXT[zustand] ?? zustand;
  if (zustand === 'geprueft') return { zustand: 'gut', text };
  if (inArbeit(zustand)) return { zustand: 'info', text };
  if (zustand === 'nicht_gefunden') return { zustand: 'warnung', text };
  return { zustand: 'neutral', text };
}

/** Die Güte eines Vorschlags als Marke - „hoch“ belegt, „unsicher“ ein zweiter Blick. */
export function gueteStatus(guete: string): Fachstatus {
  if (guete === 'hoch') return { zustand: 'gut', text: 'hoch' };
  if (guete === 'auswahl') return { zustand: 'info', text: 'Auswahl' };
  if (guete === 'mittel') return { zustand: 'neutral', text: 'mittel' };
  return { zustand: 'warnung', text: guete };
}

/** KB, ganzzahlig deutsch. */
export function kb(bytes: number | null | undefined): string {
  return `${formatiereZahl(Math.round((bytes ?? 0) / 1024), 0)} KB`;
}
